import { useMediaQuery } from '@mui/material';

/**
 * Layout thresholds for the admin dashboard.
 *
 * These are deliberately kept separate from MUI's `theme.breakpoints`: the
 * default MUI values (sm 600 / md 900 / lg 1200 / xl 1536) are already baked
 * into every `<Grid xs md lg>` in the app, so redefining them would silently
 * reflow hundreds of components. These thresholds describe the *shell*
 * instead — how much room the page toolbars and the side drawer have to work
 * with.
 *
 * Below MOBILE the dashboard is not rendered at all (see MobileDownloadGate),
 * so NARROW is the real floor we design for.
 */
export const LAYOUT_BREAKPOINTS = {
  /** Below this the app shows the "download the app" gate instead. */
  mobile: 900,
  /** Laptops at their smallest usable width — the drawer must collapse. */
  narrow: 1024,
  /** 13"/14" laptops: page toolbars must shed labels to fit on one row. */
  compact: 1280,
  /** Above this everything fits at its natural size. */
  roomy: 1440,
} as const;

type Breakpoints = {
  /** < 1024px — drawer collapsed, toolbars stacked. */
  isNarrow: boolean;
  /** < 1280px — toolbars fall back to icon-only buttons. */
  isCompact: boolean;
  /** >= 1440px — no compromises needed. */
  isRoomy: boolean;
  /**
   * < 1440px — the side drawer should start collapsed.
   *
   * Deliberately a wider threshold than `isCompact`: the drawer is the
   * cheapest 250px to reclaim, so it is given up before the toolbars start
   * dropping their labels.
   */
  shouldCollapseDrawer: boolean;
};

/**
 * Shell-level responsive state. Prefer this over ad-hoc `useMediaQuery`
 * strings so the thresholds stay consistent across pages.
 */
export const useBreakpoints = (): Breakpoints => {
  const isNarrow = useMediaQuery(`(max-width:${LAYOUT_BREAKPOINTS.narrow - 1}px)`);
  const isCompact = useMediaQuery(`(max-width:${LAYOUT_BREAKPOINTS.compact - 1}px)`);
  const isRoomy = useMediaQuery(`(min-width:${LAYOUT_BREAKPOINTS.roomy}px)`);

  return { isNarrow, isCompact, isRoomy, shouldCollapseDrawer: !isRoomy };
};

export default useBreakpoints;
